<template>
  <button
    class="button button--clear designbutton"
    type="button"
    :class="{
      designbutton_borderless: borderless,
      invisible: isInvisible,
      designbutton_flat: buttonStyle == 'flat'
    }"
    @click="onClick($event)"
  >
    <slot :color="(color as EmittableColor)" :is-invisible="isInvisible">
      <template v-if="isSymbol">
        <div v-if="buttonStyle == 'symbol'" class="designbutton__symbolblock">
          <template v-if="!filled">
            <svg-symbol
              :name="symbolFilled"
              class="designbutton__symbol"
              :style="{ color: color }"
            />
            <svg-symbol
              :name="symbolFilled"
              class="designbutton__symbol"
              :style="{ color: 'var(--color-background-deep)', opacity: 0.7 }"
            />
            <svg-symbol
              :name="standardSymbol"
              class="designbutton__symbol"
              :style="{ color: color }"
            />
          </template>
          <svg-symbol
            v-else
            :name="symbolFilled"
            class="designbutton__symbol"
            :style="{ color: color }"
          />
        </div>
        <template v-else>
          <lucide-icon v-if="isInvisible" name="eye-off" />
          <div
            v-else
            :style="{ color: color || 'transparent' }"
            class="designbutton__bg"
            :class="{ '-filled': symbolFilled }"
          ></div>
        </template>
      </template>
      <lucide-icon
        v-else
        :name="icon"
        :style="{ color: color }"
        class="designbutton__symbol is-eye"
      />
    </slot>

    <color-picker-popout
      v-if="colorPicker.open"
      :value="color"
      :x="colorPicker.x"
      :y="colorPicker.y"
      :extra-colors="extraColors"
      :null-allowed="nullAllowed"
      @update:value="throttledUpdateColor?.($event)"
      @close="colorPicker.open = false"
    />

    <symbol-picker-popout
      v-if="symbolPicker.open"
      :value="symbol || standardSymbol"
      :x="symbolPicker.x"
      :y="symbolPicker.y"
      @update:value="$emit('update:symbol', $event)"
      @close="symbolPicker.open = false"
    />
  </button>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { throttle } from "lodash-es";

import SvgSymbol from "@core/ui/SvgSymbol.vue";
import ColorPickerPopout from "@core/ui/ColorPickerPopout.vue";
import SymbolPickerPopout from "@core/ui/SymbolPickerPopout.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import type {
  SymbolName,
  UnfilledSymbolName
} from "@core/ui/svg-icons/symbols/SymbolName";

interface Props {
  filled?: boolean;
  symbol?: UnfilledSymbolName | null;
  color?: string | null;
  type?: "symbol" | "color" | "visible";
  buttonStyle?: "symbol" | "flat";
  borderless?: boolean;
  hidden?: boolean;
  extraColors?: string[];
  nullAllowed?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  filled: false,
  symbol: null,
  color: "rgba(0, 0, 0, 1)",
  type: "color",
  buttonStyle: "symbol",
  borderless: false,
  hidden: false,
  extraColors: () => [],
  nullAllowed: false
});

// Allow emitting null if "none" prop is set.
type C<T extends boolean> = T extends true ? string | null : string;
type EmittableColor = C<typeof props.nullAllowed>;

const emit = defineEmits<{
  (e: "update:color", color: EmittableColor): void;
  (e: "update:symbol", symbol: UnfilledSymbolName): void;
  (e: "update:hidden", hidden: boolean): void;
}>();

// DATA

const colorPicker = reactive({
  open: false,
  x: 0,
  y: 0
});
const symbolPicker = reactive({
  open: false,
  x: 0,
  y: 0
});

const standardSymbol = computed(() => {
  if (props.symbol) return props.symbol;
  return "circle";
});
const isInvisible = computed(() => {
  if (props.hidden) return true;
  if (!props.color) return false;
  return props.color.replaceAll(" ", "") === "rgba(0,0,0,0)";
});
const isSymbol = computed(() => {
  return !isInvisible.value && props.type !== "visible";
});
const icon = computed(() => {
  if (isInvisible.value) {
    return "eye-off";
  } else {
    return "eye";
  }
});
const symbolFilled = computed((): SymbolName => {
  return `${standardSymbol.value}-filled`;
});

// METHODS

const throttledUpdateColor = throttle((color: EmittableColor) => {
  emit("update:color", color);
}, 500);

function onClick(mouseEvent: MouseEvent) {
  if (props.type === "color") {
    chooseColor(mouseEvent);
  } else if (props.type === "symbol") {
    chooseSymbol(mouseEvent);
  } else if (props.type === "visible") {
    emit("update:hidden", !isInvisible.value);
  }
}

function chooseColor(mouseEvent: MouseEvent) {
  const target = mouseEvent.target as HTMLElement;
  const buttonRect =
    target.closest("button")?.getBoundingClientRect?.() ||
    target.getBoundingClientRect();
  colorPicker.open = true;
  colorPicker.x = buttonRect.left;
  colorPicker.y = buttonRect.top;
}

function chooseSymbol(mouseEvent: MouseEvent) {
  const target = mouseEvent.target as HTMLElement;
  const buttonRect =
    target.closest("button")?.getBoundingClientRect?.() ||
    target.getBoundingClientRect();
  symbolPicker.open = true;
  symbolPicker.x = buttonRect.left;
  symbolPicker.y = buttonRect.top;
}
</script>

<style lang="scss">
.designbutton {
  padding: 6px;
  height: 32px;
  width: 32px;
  flex-shrink: 0;

  &__symbol {
    pointer-events: none;
    font-size: 20px;
    max-height: 20px;
    line-height: 20px;
    display: block;
    grid-area: symbol;
    &.is-eye {
      color: var(--color-text-medium) !important;
    }
    &:last-child {
      z-index: 1;
    }
  }
  &__symbolblock {
    position: relative;
    display: grid;
    grid-template-areas: "symbol";
    z-index: 0;
    height: 20px;
    width: 20px;
  }

  &_borderless {
    border: none;
  }

  &.invisible {
    background-color: var(--color-background-medium);
  }

  &__bg {
    width: 80%;
    height: 80%;
    border-radius: inherit;
    transition: box-shadow 250ms ease;
    background-color: currentColor;
    border: 2px solid currentColor;
    box-shadow: 0 1px 3px var(--color-shadow-1);
    position: relative;
    overflow: hidden;
    &:hover:not(:disabled) {
      box-shadow: 0 2px 4px var(--color-shadow-2);
    }
    &::after {
      content: "";
      display: block;
      background-color: rgba(255, 255, 255, 0.7);
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      position: absolute;
    }
    &.-filled::after {
      display: none;
    }
  }

  &_flat {
    padding: 0;
    overflow: hidden;
    flex-shrink: 0;
    border: none;
    font-size: 16px;
  }
}
</style>
